import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  SubmitApplicationInfo,
  ApplicationV2Extras,
  PortfolioVideoItem,
} from "./creatorApplicationTypesV2";
import { boolean } from "zod";

/** helper to avoid Draft index => never issues */
function setKey<T extends object, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K],
) {
  obj[key] = value;
}

type UpdatePortfolioPayload = {
  id: string;
  patch: Partial<PortfolioVideoItem>;
};
export interface CreatorApplicationV2State {
  currentStep: number;
  SubmitApplicationInfo: SubmitApplicationInfo; // OLD payload
  v2Extras: ApplicationV2Extras; // NEW (V2 UI only)
}

const initialState: CreatorApplicationV2State = {
  currentStep: 1,

  SubmitApplicationInfo: {
    fullName: "",
    gender: "",
    age: null,
    phoneNumber: "",
    country: "",
    city: "",
    state: "",
    address: "",
    brandsDontWantToWorkWith: "",
    skinTone: "",
    bodyType: "",
    hasRelativesInVideo: undefined,

    profileLink: "",
    experienceDescription: "",
    experience: "",
    previousVideos: [],
    videoLinks: [],
    topics: "",
    skills: "",

    availableHoursPerWeek: "",
    weekendAvailability: null,
    language: "",
    lastSalary: "",
    expectedPay: "",

    wardrobeStyle: [],
    backgroundStyle: [],

    propsAvailable: "",
    greenScreenDetails: null,
    audioEquipmentDetails: { hasAudioEquipment: null, details: "" },
    cameraDetails: { hasCamera: null, details: "" },
    lightingDetails: { hasLighting: null, details: "" },
    additionalExperience: { hasAdditionalExperience: null, details: "" },
  },

  v2Extras: {
    canBringToVideo: "",
    // Step 2
    profilePhoto: null,

    // Step 3
    pincode: "",
    mapLink: "",
    languages: [], // ✅ NEW
    lastDrawnSalary: "",
    expectedPayout: "",
    expectedPayoutType: "",

    // Step 4
    skillsMulti: [],
    customSkills: [],
    profileLinks: [],
    comfortableWith: {},
    avoidTopics: [],
    portfolioVideos: [],
    availableHoursPerWeek: "",
    availabilityForWeekendsOrEvenings: false,

    // Step 5
    coreSetup: [],
    whoCanAppear: [],

    // Step 6
    wardrobeImages: [],
    backgroundImages: [],
  },
};

const creatorApplicationSliceV2 = createSlice({
  name: "signUpV2",
  initialState,
  reducers: {
    /* ---------- STEP NAV ---------- */
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    nextStep(state) {
      state.currentStep += 1;
    },
    prevStep(state) {
      state.currentStep -= 1;
    },

    /* ---------- UPDATE OLD PAYLOAD FIELD ---------- */
    updateField: {
      reducer(
        state,
        action: PayloadAction<{
          field: keyof SubmitApplicationInfo;
          value: unknown;
        }>,
      ) {
        const { field, value } = action.payload;
        setKey(
          state.SubmitApplicationInfo,
          field,
          value as SubmitApplicationInfo[typeof field],
        );
      },
      prepare<K extends keyof SubmitApplicationInfo>(
        field: K,
        value: SubmitApplicationInfo[K],
      ) {
        return { payload: { field, value } };
      },
    },

    /* ---------- UPDATE V2 EXTRA FIELD ---------- */
    updateV2Field: {
      reducer(
        state,
        action: PayloadAction<{
          field: keyof ApplicationV2Extras;
          value: unknown;
        }>,
      ) {
        const { field, value } = action.payload;
        setKey(
          state.v2Extras,
          field,
          value as ApplicationV2Extras[typeof field],
        );
      },
      prepare<K extends keyof ApplicationV2Extras>(
        field: K,
        value: ApplicationV2Extras[K],
      ) {
        return { payload: { field, value } };
      },
    },
    updateV2FieldKeyPair: {
      reducer(
        state,
        action: PayloadAction<{
          key: keyof ApplicationV2Extras;
          value: unknown;
        }>,
      ) {
        const { key, value } = action.payload;
        setKey(state.v2Extras, key, value as ApplicationV2Extras[typeof key]);
      },

      prepare<K extends keyof ApplicationV2Extras>(payload: {
        key: K;
        value: ApplicationV2Extras[K];
      }) {
        return { payload };
      },
    },

    /* ---------- UPDATE NESTED V2 FIELD (profileLinks etc) ---------- */
    // updateV2NestedField: {
    //   reducer(
    //     state,
    //     action: PayloadAction<{
    //       parent: "profileLinks";
    //       field: string;
    //       value: unknown;
    //     }>,
    //   ) {
    //     const { parent, field, value } = action.payload;
    //     // parent restricted to profileLinks so it can't be misused
    //     (state.v2Extras[parent] as Record<string, unknown>)[field] = value;
    //   },
    //   prepare<F extends keyof ApplicationV2Extras["profileLinks"]>(
    //     parent: "profileLinks",
    //     field: F,
    //     value: ApplicationV2Extras["profileLinks"][F],
    //   ) {
    //     return { payload: { parent, field: String(field), value } };
    //   },
    // },

    /* ---------------- PORTFOLIO VIDEOS (V2) ---------------- */

    addPortfolioVideo(state, action: PayloadAction<PortfolioVideoItem>) {
      state.v2Extras.portfolioVideos.unshift(action.payload); // newest first
    },

    updatePortfolioVideo(state, action: PayloadAction<UpdatePortfolioPayload>) {
      const { id, patch } = action.payload;
      const idx = state.v2Extras.portfolioVideos.findIndex((v) => v.id === id);
      if (idx === -1) return;
      state.v2Extras.portfolioVideos[idx] = {
        ...state.v2Extras.portfolioVideos[idx],
        ...patch,
      };
    },

    removePortfolioVideo(state, action: PayloadAction<{ id: string }>) {
      state.v2Extras.portfolioVideos = state.v2Extras.portfolioVideos.filter(
        (v) => v.id !== action.payload.id,
      );
    },

    // optional: reorder (drag n drop)
    reorderPortfolioVideos(
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) {
      const { fromIndex, toIndex } = action.payload;
      const arr = state.v2Extras.portfolioVideos;
      if (fromIndex < 0 || fromIndex >= arr.length) return;
      if (toIndex < 0 || toIndex >= arr.length) return;

      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, moved);
    },

    // optional: clear all
    clearPortfolioVideos(state) {
      state.v2Extras.portfolioVideos = [];
    },
    resetApplicationForm() {
      return initialState;
    },
  },
});

export const {
  setCurrentStep,
  nextStep,
  prevStep,
  updateField,
  updateV2Field,
  updateV2FieldKeyPair,
  // updateV2NestedField,
  resetApplicationForm,

  addPortfolioVideo,
  updatePortfolioVideo,
  removePortfolioVideo,
  reorderPortfolioVideos,
  clearPortfolioVideos,
} = creatorApplicationSliceV2.actions;

export default creatorApplicationSliceV2.reducer;
